import moment from 'moment-timezone'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { CAMPAIGN_STATUS } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { API } from '@/client/utils/api'
import {
  getNttAaaSwitchoverAt,
  isPeriodEndAfterNttAaaSwitchover,
  isPeriodStartBeforeNttAaaSwitchover
} from '@/common/utils'
import { CampaignAccount, GetCampaignAccountsResponse } from '@/typings/campaign_accounts'
import {
  Campaign,
  DeleteCampaignResponse,
  GetCampaignByIdResponse,
  GetValidationResponse,
  PostCampaignResponse,
  PutCampaignResponse,
  WebInstantwinBasicLayout,
  WebInstantwinCustomLayout,
  WebInstantwinCustomLayoutSectionMedia,
  WebInstantwinCustomLayoutSectionRule,
  WebInstantwinLayoutType,
  WebInstantwinTwSettingPayload
} from '@/typings/campaigns'
import { GetProfileKeywordSettingsResponse } from '@/typings/profile_keyword_settings'

import { useSystemStore } from './useSystemStore'

export type CampaignForm = {
  title: string
  account_id: string | number
  target_sns: string
  start_date: string | null
  start_time: string | null
  end_date: string | null
  end_time: string | null
  entry_action_type: string
  campaign_type?: string
  keywords: string[]
  or_keywords?: string[] | null
  positive_keyword?: string[]
  negative_keyword?: string[]
  is_include_mention?: boolean
  is_include_reply?: boolean
  is_keyword_judgment?: boolean
  require_follow?: boolean
  exclude_hunter_account?: boolean
  exclude_account_create_date_within_period?: boolean
  exclude_default_profile_image?: boolean
  exclude_no_description?: boolean
  exclude_unfollowed?: boolean
  min_followers?: number
  daily_send_limit_count?: number
  min_tweets?: number
  exclude_description_keyword?: string[]
  layout_type?: WebInstantwinLayoutType
  custom_layout?: WebInstantwinCustomLayout
  page_main_image_url?: string | null
  page_main_image_origin_name?: string | null
  page_title?: string
  page_text?: string
  page_rules?: string
  page_hash?: string
  is_daily_lottery?: boolean
  exclude_multi_winner?: boolean
  exclude_other_campaign_winner?: boolean
  is_browser_guide_enabled?: boolean
}

export type CampaignDialogState = {
  api_accounts: CampaignAccount[]
  api_campaign: Campaign | null
  campaign_id: number | null
  form: CampaignForm
  mode: 'create' | 'edit' | 'copy'
  step: number
  is_loading: boolean
  is_max_concurrent_campaign: boolean
  is_validating: boolean
  webiw_background_color_show_hex: boolean
}

// --- WebInstantwin layout constants ---

const WEB_INSTANTWIN_DEFAULT_MEDIA_SIZE = 800
const DEFAULT_RULE_SECTION_BACKGROUND_COLOR = '#ffffff'
const DEFAULT_BODY_BACKGROUND_COLOR = '#ffffff'
const DEFAULT_FOOTER_TEXT_COLOR = '#333333'
const DEFAULT_LOGO_HEADER_PC_HEIGHT = 64
const DEFAULT_LOGO_HEADER_SP_HEIGHT = 64
const DEFAULT_APPLY_BUTTON_BACKGROUND_COLOR = '#009ccb'
const DEFAULT_APPLY_BUTTON_TEXT_COLOR = '#ffffff'
const DEFAULT_APPLY_TEXT_COLOR = '#000000'
const DEFAULT_APPLY_WARNING_TEXT_COLOR = '#ffa726'
const DEFAULT_APPLY_ERROR_TEXT_COLOR = '#ef5350'
const DEFAULT_RULE_BUTTON_BACKGROUND_COLOR = '#ffffff'
const DEFAULT_RULE_BUTTON_TEXT_COLOR = '#333333'

// --- WebInstantwin factory functions ---

const createWebInstantwinMediaSection = (stepOrder: number): WebInstantwinCustomLayoutSectionMedia => ({
  mode: 'media',
  step_order: stepOrder,
  pc_media_name: null,
  pc_media_url: null,
  pc_media_size: WEB_INSTANTWIN_DEFAULT_MEDIA_SIZE,
  sp_media_name: null,
  sp_media_url: null,
  url: null,
  background_color: null
})

const createWebInstantwinRuleSection = (
  stepOrder: number,
  pageRules: string
): WebInstantwinCustomLayoutSectionRule => ({
  mode: 'rule',
  step_order: stepOrder,
  page_rules: pageRules,
  background_color: DEFAULT_RULE_SECTION_BACKGROUND_COLOR,
  apply_button_background_color: DEFAULT_APPLY_BUTTON_BACKGROUND_COLOR,
  apply_button_text_color: DEFAULT_APPLY_BUTTON_TEXT_COLOR,
  apply_text_color: DEFAULT_APPLY_TEXT_COLOR,
  apply_warning_text_color: DEFAULT_APPLY_WARNING_TEXT_COLOR,
  apply_error_text_color: DEFAULT_APPLY_ERROR_TEXT_COLOR,
  rule_button_background_color: DEFAULT_RULE_BUTTON_BACKGROUND_COLOR,
  rule_button_text_color: DEFAULT_RULE_BUTTON_TEXT_COLOR
})

const createWebInstantwinDefaultCustomLayout = (pageRules: string): WebInstantwinCustomLayout => ({
  ogp_title: '',
  ogp_main_image_url: '',
  ogp_main_image_origin_name: '',
  logo_image_url: null,
  logo_image_origin_name: null,
  logo_url: null,
  logo_background_color: null,
  logo_is_enabled: false,
  logo_header_pc_height: DEFAULT_LOGO_HEADER_PC_HEIGHT,
  logo_header_sp_height: DEFAULT_LOGO_HEADER_SP_HEIGHT,
  body_background_color: DEFAULT_BODY_BACKGROUND_COLOR,
  footer_title: '',
  footer_background_color: null,
  footer_text: DEFAULT_FOOTER_TEXT_COLOR,
  sections: [createWebInstantwinMediaSection(1), createWebInstantwinRuleSection(2, pageRules)]
})

// --- WebInstantwin normalization helpers ---

const parsePcMediaSize = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '' && /^[0-9]+$/.test(value)) {
    return Number(value)
  }

  return null
}

const normalizePcMediaSize = (value: unknown) => {
  const parsed = parsePcMediaSize(value)

  if (!parsed || parsed <= 0) {
    return WEB_INSTANTWIN_DEFAULT_MEDIA_SIZE
  }

  return parsed
}

const normalizeOptionalUrl = (value?: string | null) => {
  if (typeof value !== 'string') {
    return value ?? null
  }

  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

const normalizeOptionalColor = (value?: string | null) => {
  if (typeof value !== 'string') {
    return value ?? null
  }

  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

const normalizeColorWithFallback = (value: string | null | undefined, fallback: string) =>
  normalizeOptionalColor(value) ?? fallback

const normalizeHeaderHeight = (value: unknown, fallback: number) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '' && /^[0-9]+$/.test(value)) {
    const parsed = Number(value)
    if (parsed > 0) {
      return parsed
    }
  }

  return fallback
}

const normalizeRuleSectionColors = (section: WebInstantwinCustomLayoutSectionRule) => ({
  ...section,
  apply_button_background_color: normalizeColorWithFallback(
    section.apply_button_background_color,
    DEFAULT_APPLY_BUTTON_BACKGROUND_COLOR
  ),
  apply_button_text_color: normalizeColorWithFallback(section.apply_button_text_color, DEFAULT_APPLY_BUTTON_TEXT_COLOR),
  apply_text_color: normalizeColorWithFallback(section.apply_text_color, DEFAULT_APPLY_TEXT_COLOR),
  apply_warning_text_color: normalizeColorWithFallback(
    section.apply_warning_text_color,
    DEFAULT_APPLY_WARNING_TEXT_COLOR
  ),
  apply_error_text_color: normalizeColorWithFallback(section.apply_error_text_color, DEFAULT_APPLY_ERROR_TEXT_COLOR),
  rule_button_background_color: normalizeColorWithFallback(
    section.rule_button_background_color,
    DEFAULT_RULE_BUTTON_BACKGROUND_COLOR
  ),
  rule_button_text_color: normalizeColorWithFallback(section.rule_button_text_color, DEFAULT_RULE_BUTTON_TEXT_COLOR),
  background_color: normalizeOptionalColor(section.background_color)
})

// --- WebInstantwin resolution functions ---

const resolveLogoEnabled = (customLayout: WebInstantwinCustomLayout) => {
  const logoImageUrl = normalizeOptionalUrl(customLayout.logo_image_url)
  const logoUrl = normalizeOptionalUrl(customLayout.logo_url)
  const logoBackgroundColor = normalizeOptionalColor(customLayout.logo_background_color)

  return Boolean(logoImageUrl || logoUrl || logoBackgroundColor)
}

const resolveWebInstantwinLayoutType = (
  layoutType?: WebInstantwinLayoutType,
  customLayout?: WebInstantwinCustomLayout | null
) => {
  if (layoutType === 'basic' || layoutType === 'custom') {
    return layoutType
  }

  if (customLayout) {
    return 'custom'
  }

  return 'basic'
}

// --- WebInstantwin normalization functions ---

const normalizeWebInstantwinCustomLayout = (
  customLayout?: WebInstantwinCustomLayout | null,
  defaultPageRules?: string
): WebInstantwinCustomLayout => {
  if (!customLayout) {
    return createWebInstantwinDefaultCustomLayout(defaultPageRules || '')
  }

  const normalizedSections: Array<WebInstantwinCustomLayoutSectionMedia | WebInstantwinCustomLayoutSectionRule> = (
    customLayout.sections || []
  ).map(section => {
    if (section.mode === 'media') {
      return {
        ...section,
        pc_media_size: normalizePcMediaSize(section.pc_media_size),
        url: normalizeOptionalUrl(section.url),
        background_color: normalizeOptionalColor(section.background_color)
      }
    }

    return normalizeRuleSectionColors(section)
  })

  const hasMedia = normalizedSections.some(section => section.mode === 'media')
  const hasRule = normalizedSections.some(section => section.mode === 'rule')
  let nextStepOrder = normalizedSections.reduce((max, section) => Math.max(max, section.step_order), 0) + 1

  if (!hasMedia) {
    normalizedSections.push(createWebInstantwinMediaSection(nextStepOrder))
    nextStepOrder += 1
  }

  if (!hasRule) {
    normalizedSections.push(createWebInstantwinRuleSection(nextStepOrder, defaultPageRules || ''))
  }

  const logoImageUrl = normalizeOptionalUrl(customLayout.logo_image_url)
  const logoUrl = normalizeOptionalUrl(customLayout.logo_url)
  const logoBackgroundColor = normalizeOptionalColor(customLayout.logo_background_color)
  const footerTitle = customLayout.footer_title ?? ''
  const footerBackgroundColor = normalizeOptionalColor(customLayout.footer_background_color)
  const footerTextColor = normalizeOptionalColor(customLayout.footer_text)
  const logoHeaderPcHeight = normalizeHeaderHeight(customLayout.logo_header_pc_height, DEFAULT_LOGO_HEADER_PC_HEIGHT)
  const logoHeaderSpHeight = normalizeHeaderHeight(customLayout.logo_header_sp_height, DEFAULT_LOGO_HEADER_SP_HEIGHT)
  const bodyBackgroundColor = normalizeColorWithFallback(
    customLayout.body_background_color,
    DEFAULT_BODY_BACKGROUND_COLOR
  )
  const logoIsEnabled = resolveLogoEnabled({
    ...customLayout,
    logo_image_url: logoImageUrl,
    logo_url: logoUrl,
    logo_background_color: logoBackgroundColor
  })

  return {
    ogp_title: customLayout.ogp_title || '',
    ogp_main_image_url: customLayout.ogp_main_image_url || '',
    ogp_main_image_origin_name: customLayout.ogp_main_image_origin_name || '',
    logo_image_url: logoImageUrl,
    logo_image_origin_name: customLayout.logo_image_origin_name ?? null,
    logo_url: logoUrl,
    logo_background_color: logoBackgroundColor,
    logo_is_enabled: logoIsEnabled,
    logo_header_pc_height: logoHeaderPcHeight,
    logo_header_sp_height: logoHeaderSpHeight,
    body_background_color: bodyBackgroundColor,
    footer_title: footerTitle,
    footer_background_color: footerBackgroundColor,
    footer_text: footerTextColor,
    sections: normalizedSections
  }
}

const normalizeWebInstantwinCustomLayoutForPayload = (
  customLayout?: WebInstantwinCustomLayout | null
): WebInstantwinCustomLayout => {
  if (!customLayout) {
    return createWebInstantwinDefaultCustomLayout('')
  }

  const logoImageUrl = normalizeOptionalUrl(customLayout.logo_image_url)
  const logoUrl = normalizeOptionalUrl(customLayout.logo_url)
  const logoBackgroundColor = normalizeOptionalColor(customLayout.logo_background_color)
  const footerTitle = customLayout.footer_title ?? ''
  const footerBackgroundColor = normalizeOptionalColor(customLayout.footer_background_color)
  const footerTextColor = normalizeOptionalColor(customLayout.footer_text)
  const logoHeaderPcHeight = normalizeHeaderHeight(customLayout.logo_header_pc_height, DEFAULT_LOGO_HEADER_PC_HEIGHT)
  const logoHeaderSpHeight = normalizeHeaderHeight(customLayout.logo_header_sp_height, DEFAULT_LOGO_HEADER_SP_HEIGHT)
  const bodyBackgroundColor = normalizeColorWithFallback(
    customLayout.body_background_color,
    DEFAULT_BODY_BACKGROUND_COLOR
  )
  const logoIsEnabled = resolveLogoEnabled({
    ...customLayout,
    logo_image_url: logoImageUrl,
    logo_url: logoUrl,
    logo_background_color: logoBackgroundColor
  })

  return {
    ogp_title: customLayout.ogp_title || '',
    ogp_main_image_url: customLayout.ogp_main_image_url || '',
    ogp_main_image_origin_name: customLayout.ogp_main_image_origin_name || '',
    logo_image_url: logoImageUrl,
    logo_image_origin_name: customLayout.logo_image_origin_name ?? null,
    logo_url: logoUrl,
    logo_background_color: logoBackgroundColor,
    logo_is_enabled: logoIsEnabled,
    logo_header_pc_height: logoHeaderPcHeight,
    logo_header_sp_height: logoHeaderSpHeight,
    body_background_color: bodyBackgroundColor,
    footer_title: footerTitle,
    footer_background_color: footerBackgroundColor,
    footer_text: footerTextColor,
    sections: (customLayout.sections || []).map(section => {
      if (section.mode === 'media') {
        return {
          ...section,
          pc_media_size: normalizePcMediaSize(section.pc_media_size),
          url: normalizeOptionalUrl(section.url),
          background_color: normalizeOptionalColor(section.background_color)
        }
      }

      return normalizeRuleSectionColors(section)
    })
  }
}

// --- WebInstantwin validation functions ---

const isValidHttpUrl = (url?: string | null) => {
  if (!url) {
    return true
  }

  return /^https?:\/\//.test(url)
}

const isValidPcMediaSize = (value: unknown) => {
  const parsed = parsePcMediaSize(value)
  return Boolean(parsed && parsed > 0)
}

const isValidWebInstantwinBasicLayout = (form: CampaignForm) =>
  Boolean(
    form.page_title && form.page_title.length <= 50 && form.page_text && form.page_rules && form.page_main_image_url
  )

const isValidWebInstantwinCustomLayout = (customLayout?: WebInstantwinCustomLayout | null) => {
  if (!customLayout) {
    return false
  }

  if (!customLayout.ogp_title || !customLayout.ogp_title.trim()) {
    return false
  }

  if (!customLayout.ogp_main_image_url || !customLayout.ogp_main_image_origin_name) {
    return false
  }

  if (!isValidHttpUrl(customLayout.logo_url)) {
    return false
  }

  if (!customLayout.body_background_color || !customLayout.body_background_color.trim()) {
    return false
  }

  const sections = customLayout.sections || []
  if (!sections.length) {
    return false
  }

  const ruleSections = sections.filter(
    (section): section is WebInstantwinCustomLayoutSectionRule => section.mode === 'rule'
  )
  const mediaSections = sections.filter(
    (section): section is WebInstantwinCustomLayoutSectionMedia => section.mode === 'media'
  )

  if (ruleSections.length !== 1 || mediaSections.length === 0) {
    return false
  }

  const isMediaValid = mediaSections.every(section => {
    if (!section.pc_media_url) {
      return false
    }

    if (!section.sp_media_url) {
      return false
    }

    if (!isValidPcMediaSize(section.pc_media_size)) {
      return false
    }

    return isValidHttpUrl(section.url)
  })

  if (!isMediaValid) {
    return false
  }

  return ruleSections.every(section => Boolean(section.page_rules && section.page_rules.trim()))
}

/**
 * フォロー&リポスト系の応募アクション（公式リポスト / 引用 / 全て）かどうか。
 * この系統はキャンペーンキーワードを必須としない。
 */
const isRetweetActionType = (entry_action_type: string) => {
  return ['retweet', 'quote_tweet', 'retweet_and_quote_tweet'].includes(entry_action_type)
}

const isWebInstantwinFormInvalid = (form: CampaignForm) => {
  if (form.campaign_type !== 'old_web_instantwin') {
    return false
  }

  const layoutType = form.layout_type || 'basic'

  if (layoutType === 'custom') {
    return !isValidWebInstantwinCustomLayout(form.custom_layout)
  }

  return !isValidWebInstantwinBasicLayout(form)
}

// --- WebInstantwin payload building functions ---

const buildWebInstantwinBasicLayout = (form: CampaignForm): WebInstantwinBasicLayout => ({
  page_title: form.page_title || '',
  page_main_image_url: form.page_main_image_url ? form.page_main_image_url : undefined,
  page_main_image_origin_name: form.page_main_image_origin_name ? form.page_main_image_origin_name : null,
  page_text: form.page_text || '',
  page_rules: form.page_rules || '',
  is_browser_guide_enabled: form.is_browser_guide_enabled ?? false
})

const buildWebInstantwinCustomLayoutPayload = (
  customLayout?: WebInstantwinCustomLayout | null
): WebInstantwinCustomLayout => normalizeWebInstantwinCustomLayoutForPayload(customLayout)

const buildWebInstantwinLayoutPayload = (form: CampaignForm): WebInstantwinTwSettingPayload => {
  const layoutType: WebInstantwinLayoutType = form.layout_type || 'basic'

  if (layoutType === 'custom') {
    return {
      layout_type: 'custom',
      custom_layout: buildWebInstantwinCustomLayoutPayload(form.custom_layout)
    }
  }

  return {
    layout_type: 'basic',
    basic_layout: buildWebInstantwinBasicLayout(form)
  }
}

let page_rules = '・特典の進呈はお一人様1回までとなります。複数アカウントの利用はご遠慮ください。\n'
page_rules +=
  '・不正に複数アカウントを利用し参加されたことが発覚した場合は、商品を無効化するなどの対応を取る場合がございます。ご了承ください。\n'
page_rules +=
  '・ギフトは、ギフト詳細に記載された有効期限までに商品と交換してください。有効期限を過ぎた場合は無効となります。\n'
page_rules += '・商品の譲渡・転売・換金することを固く禁止いたします。\n'
page_rules += '・本キャンペーンは予告なく中止、終了、内容の変更等をする場合がございます。\n'
page_rules += '・当社は、本キャンペーンに関連して応募者、その他第三者に発生した損害等について、一切責任を負いません。\n'

const initial_form: CampaignForm = {
  title: '',
  account_id: '',
  target_sns: 'twitter',
  start_date: null,
  start_time: null,
  end_date: null,
  end_time: null,
  campaign_type: 'keyword',
  entry_action_type: 'retweet',
  keywords: [],
  or_keywords: [],
  positive_keyword: [],
  negative_keyword: [],
  is_include_mention: true,
  is_include_reply: true,
  is_keyword_judgment: true,
  require_follow: true,
  exclude_hunter_account: true,
  exclude_account_create_date_within_period: true,
  exclude_default_profile_image: true,
  exclude_no_description: true,
  exclude_unfollowed: true,
  min_followers: 0,
  daily_send_limit_count: 10,
  min_tweets: 0,
  exclude_description_keyword: [],
  layout_type: 'basic',
  custom_layout: createWebInstantwinDefaultCustomLayout(page_rules),
  page_title: '',
  page_main_image_url: '',
  page_main_image_origin_name: '',
  page_text: '',
  page_rules,
  is_daily_lottery: true,
  exclude_multi_winner: true,
  exclude_other_campaign_winner: true,
  is_browser_guide_enabled: false
}

let default_positive_keyword: string[] = []
let default_negative_keyword: string[] = []

export const useCampaignDialogStore = defineStore('campaignDialogStore', () => {
  const systemStore = useSystemStore()

  const api_accounts = ref<CampaignAccount[]>([])
  const api_campaign = ref<Campaign | null>(null)
  const campaign_id = ref<number | null>(null)
  const form = ref<CampaignForm>({ ...initial_form })
  const mode = ref<'create' | 'edit' | 'copy'>('create')
  const step = ref(0)
  const is_loading = ref(false)
  const is_max_concurrent_campaign = ref(false)
  const is_validating = ref(false)
  const webiw_background_color_show_hex = ref(true)

  // getters (computed)
  const is_form_change = computed(() => {
    if (mode.value === 'create') {
      const init_form = {
        ...initial_form,
        account_id: api_accounts.value.length ? api_accounts.value[0].id : '',
        positive_keyword: default_positive_keyword,
        negative_keyword: default_negative_keyword
      }

      const is_change =
        init_form.title !== form.value.title ||
        init_form.account_id !== form.value.account_id ||
        init_form.target_sns !== form.value.target_sns ||
        init_form.start_date !== form.value.start_date ||
        init_form.start_time !== form.value.start_time ||
        init_form.end_date !== form.value.end_date ||
        init_form.end_time !== form.value.end_time ||
        init_form.campaign_type !== form.value.campaign_type ||
        init_form.entry_action_type !== form.value.entry_action_type ||
        init_form.keywords !== form.value.keywords ||
        init_form.or_keywords !== form.value.or_keywords ||
        init_form.positive_keyword !== form.value.positive_keyword ||
        init_form.negative_keyword !== form.value.negative_keyword ||
        init_form.is_include_mention !== form.value.is_include_mention ||
        init_form.is_include_reply !== form.value.is_include_reply

      return is_change
    }

    if (!api_campaign.value) {
      return false
    }

    const is_edit_change =
      api_campaign.value.title !== form.value.title ||
      moment(api_campaign.value.start_datetime).format('YYYY-MM-DD') !== form.value.start_date ||
      moment(api_campaign.value.start_datetime).format('HH:mm') !== form.value.start_time ||
      moment(api_campaign.value.end_datetime).format('YYYY-MM-DD') !== form.value.end_date ||
      moment(api_campaign.value.end_datetime).format('HH:mm') !== form.value.end_time

    const is_copy_change =
      api_campaign.value.title !== form.value.title ||
      (api_campaign.value.account && api_campaign.value.account.id !== form.value.account_id) ||
      form.value.start_date !== null ||
      form.value.start_time !== null ||
      form.value.end_date !== null ||
      form.value.end_time !== null

    if (api_campaign.value.target_sns === 'twitter') {
      const tw_setting = api_campaign.value.tw_setting

      const is_tw_setting_change = Boolean(
        (tw_setting && tw_setting.entry_action_type !== form.value.entry_action_type) ||
        (tw_setting && tw_setting.search_keyword !== form.value.keywords) ||
        (tw_setting && tw_setting.or_keyword !== form.value.or_keywords) ||
        (tw_setting && tw_setting.positive_keyword !== form.value.positive_keyword) ||
        (tw_setting && tw_setting.negative_keyword !== form.value.negative_keyword) ||
        (tw_setting && tw_setting.is_include_mention !== form.value.is_include_mention) ||
        (tw_setting && tw_setting.is_include_reply !== form.value.is_include_reply)
      )

      return mode.value === 'edit' ? is_edit_change || is_tw_setting_change : is_copy_change || is_tw_setting_change
    }

    if (api_campaign.value.target_sns === 'instagram') {
      if (api_campaign.value.type === 'instantwin') {
        const setting = api_campaign.value.in_instantwin_setting

        const is_instant_setting_change = Boolean(
          (setting && setting.entry_action_type !== form.value.entry_action_type) ||
          (setting && setting.search_keyword !== form.value.keywords) ||
          (setting &&
            (setting.is_daily_lottery !== form.value.is_daily_lottery ||
              setting.exclude_multi_winner !== form.value.exclude_multi_winner ||
              setting.exclude_other_campaign_winner !== form.value.exclude_other_campaign_winner))
        )

        return mode.value === 'edit'
          ? is_edit_change || is_instant_setting_change
          : is_copy_change || is_instant_setting_change
      }

      const setting = api_campaign.value.in_setting

      const is_in_setting_change = Boolean(
        (setting && setting.entry_action_type !== form.value.entry_action_type) ||
        (setting && setting.search_keyword !== form.value.keywords)
      )

      return mode.value === 'edit' ? is_edit_change || is_in_setting_change : is_copy_change || is_in_setting_change
    }

    if (api_campaign.value.target_sns === 'tiktok') {
      const tt_setting = api_campaign.value.tt_setting

      const is_tt_setting_change = Boolean(
        (tt_setting && tt_setting.entry_action_type !== form.value.entry_action_type) ||
        (tt_setting && tt_setting.search_keyword !== form.value.keywords)
      )

      return mode.value === 'edit' ? is_edit_change || is_tt_setting_change : is_copy_change || is_tt_setting_change
    }

    return false
  })

  const is_campaign_period_empty = computed(() => {
    return !form.value.start_date || !form.value.start_time || !form.value.end_date || !form.value.end_time
  })

  const is_start_datetime_error = computed(() => {
    if (!form.value.start_date || !form.value.start_time) {
      return false
    }

    if (mode.value === 'edit' && api_campaign.value && api_campaign.value.status !== CAMPAIGN_STATUS.BEFORE_EVENT) {
      return false
    }

    const start_datetime = moment(`${form.value.start_date} ${form.value.start_time}`, 'YYYY-MM-DD HH:mm')

    return start_datetime.isBefore(moment())
  })

  const is_end_datetime_before_start_datetime = computed(() => {
    if (!form.value.start_date || !form.value.start_time || !form.value.end_date || !form.value.end_time) {
      return false
    }

    const start_datetime = moment(`${form.value.start_date} ${form.value.start_time}`, 'YYYY-MM-DD HH:mm')
    const end_datetime = moment(`${form.value.end_date} ${form.value.end_time}`, 'YYYY-MM-DD HH:mm')

    return end_datetime.isSameOrBefore(start_datetime)
  })

  const is_end_date_time_before_now = computed(() => {
    if (!form.value.end_date || !form.value.end_time) {
      return false
    }

    const end_datetime = moment(`${form.value.end_date} ${form.value.end_time}`, 'YYYY-MM-DD HH:mm')

    return end_datetime.isSameOrBefore(moment())
  })

  /**
   * 応募収集 URL 設定の注意書きを表示する対象か。
   */
  const is_url_setting_notice_target = computed(() => {
    if (form.value.target_sns !== 'twitter' || form.value.campaign_type !== 'keyword') {
      return false
    }

    if (!isRetweetActionType(form.value.entry_action_type)) {
      return false
    }

    if (!form.value.end_date || !form.value.end_time) {
      return false
    }

    const end_datetime = moment(`${form.value.end_date} ${form.value.end_time}`, 'YYYY-MM-DD HH:mm')

    return isPeriodEndAfterNttAaaSwitchover(end_datetime.toDate())
  })

  /**
   * キャンペーン期間の終了日時が NTT AAA 切替日時より後か。
   * 終了日時は「未満」の指定であるため、切替日時ちょうどは切替前として扱う。
   * 期間未入力のうちは判定できないため false を返す。
   */
  const is_period_end_after_ntt_aaa_switchover = computed(() => {
    if (!form.value.end_date || !form.value.end_time) {
      return false
    }

    const end_datetime = moment(`${form.value.end_date} ${form.value.end_time}`, 'YYYY-MM-DD HH:mm')

    return isPeriodEndAfterNttAaaSwitchover(end_datetime.toDate())
  })

  /**
   * X WEB インスタントウィンとして開催できない期間が設定されているか。
   * 切替日時以降は応募を収集できないため、期間の終了日時が切替日時より後なら開催できない。
   */
  const is_web_instantwin_period_invalid = computed(() => {
    if (form.value.target_sns !== 'twitter' || form.value.campaign_type !== 'old_web_instantwin') {
      return false
    }

    return is_period_end_after_ntt_aaa_switchover.value
  })

  /** バリデーションメッセージへ埋め込む切替日時の日付表記 */
  const ntt_aaa_switchover_date_text = computed(() => {
    return moment.tz(getNttAaaSwitchoverAt(), 'Asia/Tokyo').format('YYYY年M月D日')
  })

  /**
   * キャンペーンキーワード入力を非表示にするか。
   */
  const is_keyword_input_hidden = computed(() => {
    if (form.value.target_sns !== 'twitter' || form.value.campaign_type !== 'keyword') {
      return false
    }

    if (!isRetweetActionType(form.value.entry_action_type)) {
      return false
    }

    if (!form.value.start_date || !form.value.start_time) {
      return false
    }

    const start_datetime = moment(`${form.value.start_date} ${form.value.start_time}`, 'YYYY-MM-DD HH:mm')

    return !isPeriodStartBeforeNttAaaSwitchover(start_datetime.toDate())
  })

  /**
   * キャンペーンキーワードを必須にするか。
   * リポスト・引用系は通常キャンペーンなら入力欄を隠すときだけ免除し、WEB インスタントウィンは従来どおり免除する。
   */
  const is_keyword_required = computed(() => {
    if (form.value.target_sns !== 'twitter' || !isRetweetActionType(form.value.entry_action_type)) {
      return true
    }

    return form.value.campaign_type === 'keyword' && !is_keyword_input_hidden.value
  })

  /**
   * 長文ポストの注意書きを表示する対象か。
   * キーワードで応募を収集する期間があるキャンペーンが対象のため、キーワード入力欄の表示条件にそろえる。
   */
  const is_long_post_notice_target = computed(() => {
    if (!isRetweetActionType(form.value.entry_action_type)) {
      return true
    }

    return !is_keyword_input_hidden.value
  })

  const is_next_disabled = computed(() => {
    if (is_validating.value) return true

    const account = api_accounts.value.find(account => account.id === form.value.account_id)

    if (step.value === 0 && (!account || account.is_token_expired)) {
      return true
    }

    const title = form.value.title.trim()

    if (step.value === 1 && (title.length === 0 || title.length > 50)) {
      return true
    }

    const is_invalid_period =
      is_max_concurrent_campaign.value ||
      is_campaign_period_empty.value ||
      is_start_datetime_error.value ||
      is_end_datetime_before_start_datetime.value ||
      is_end_date_time_before_now.value

    if (step.value === 2 && is_invalid_period) {
      return true
    }

    const twitter_action_types = [
      'retweet',
      'quote_tweet',
      'retweet_and_quote_tweet',
      'tweet',
      'tweet_media',
      'mention'
    ]

    const instagram_action_types = ['mention', 'comment']

    const tiktok_action_types = ['comment']

    if (
      step.value === 3 &&
      form.value.target_sns === 'twitter' &&
      form.value.campaign_type !== 'old_web_instantwin' &&
      ((!form.value.keywords.length && is_keyword_required.value) ||
        !twitter_action_types.includes(form.value.entry_action_type))
    ) {
      return true
    }

    if (
      step.value === 3 &&
      form.value.target_sns === 'twitter' &&
      form.value.campaign_type === 'old_web_instantwin' &&
      (!form.value.keywords.length || isWebInstantwinFormInvalid(form.value) || is_web_instantwin_period_invalid.value)
    ) {
      return true
    }

    if (
      step.value === 3 &&
      form.value.target_sns === 'instagram' &&
      (!form.value.keywords.length || !instagram_action_types.includes(form.value.entry_action_type))
    ) {
      return true
    }

    if (
      step.value === 3 &&
      form.value.target_sns === 'tiktok' &&
      (!form.value.keywords.length || !tiktok_action_types.includes(form.value.entry_action_type))
    ) {
      return true
    }

    return false
  })

  const is_edit_disabled = computed(() => {
    // 期間の終了日時が切替日時より後の X WEB インスタントウィンはサーバー側でも受け付けないため保存させない.
    if (is_web_instantwin_period_invalid.value) {
      return true
    }

    if (
      api_campaign.value &&
      [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
        api_campaign.value.status
      )
    ) {
      return api_campaign.value.title === form.value.title
    }

    const twitter_action_types = [
      'retweet',
      'quote_tweet',
      'retweet_and_quote_tweet',
      'tweet',
      'tweet_media',
      'mention'
    ]

    const instagram_action_types = ['mention', 'comment']

    const is_invalid_period =
      is_max_concurrent_campaign.value ||
      is_campaign_period_empty.value ||
      is_start_datetime_error.value ||
      is_end_datetime_before_start_datetime.value ||
      is_end_date_time_before_now.value

    const is_account_deleted = Boolean(!api_campaign.value || !api_campaign.value.account)

    const is_web_instantwin_invalid = isWebInstantwinFormInvalid(form.value)

    return (
      !form.value.title.trim() ||
      form.value.title.trim().length > 50 ||
      is_account_deleted ||
      is_invalid_period ||
      is_web_instantwin_invalid ||
      (!form.value.keywords.length && is_keyword_required.value) ||
      (form.value.target_sns === 'twitter' && !twitter_action_types.includes(form.value.entry_action_type)) ||
      (form.value.target_sns === 'instagram' && !instagram_action_types.includes(form.value.entry_action_type))
    )
  })

  const is_title_max_length = computed(() => {
    return form.value.title.trim().length > 50
  })

  // actions
  const fetchCampaign = async (): Promise<GetCampaignByIdResponse> => {
    is_loading.value = true

    const response: GetCampaignByIdResponse = await API.get(`v1/campaigns/${campaign_id.value}`)

    is_loading.value = false

    if (!response || !response.data) {
      return response
    }

    const data = response.data

    const settingMap = {
      twitter: data.tw_setting,
      instagram: data.type === 'instantwin' ? data.in_instantwin_setting : data.in_setting,
      tiktok: data.tt_setting
    }

    const setting = settingMap[data.target_sns]

    let form_data: CampaignForm = {
      title: data.title,
      account_id: data.account ? data.account.id : '',
      target_sns: data.target_sns,
      campaign_type: data.type,
      start_date: mode.value === 'copy' ? null : moment(data.start_datetime).format('YYYY-MM-DD'),
      start_time: mode.value === 'copy' ? null : moment(data.start_datetime).format('HH:mm'),
      end_date: mode.value === 'copy' ? null : moment(data.end_datetime).format('YYYY-MM-DD'),
      end_time: mode.value === 'copy' ? null : moment(data.end_datetime).format('HH:mm'),
      entry_action_type: setting ? setting.entry_action_type : '',
      keywords: setting ? setting.search_keyword : [],
      layout_type: 'basic',
      custom_layout: createWebInstantwinDefaultCustomLayout(page_rules)
    }

    if (data.target_sns === 'twitter' && data.tw_setting) {
      const layoutType = resolveWebInstantwinLayoutType(
        data.tw_setting.layout_type,
        data.tw_setting.custom_layout || null
      )
      const basicLayout = data.tw_setting.basic_layout
      const customLayout = normalizeWebInstantwinCustomLayout(data.tw_setting.custom_layout || null, page_rules)
      form_data = {
        ...form_data,
        or_keywords: data.tw_setting.or_keyword,
        positive_keyword: data.tw_setting.positive_keyword,
        negative_keyword: data.tw_setting.negative_keyword,
        is_include_mention: data.tw_setting.is_include_mention,
        is_include_reply: data.tw_setting.is_include_reply,
        is_keyword_judgment: Boolean(
          (data.tw_setting.positive_keyword && data.tw_setting.positive_keyword.length) ||
          (data.tw_setting.negative_keyword && data.tw_setting.negative_keyword.length)
        ),
        require_follow: data.tw_setting.require_follow,
        exclude_hunter_account: data.tw_setting.exclude_hunter_account,
        exclude_account_create_date_within_period: data.tw_setting.exclude_account_create_date_within_period,
        exclude_default_profile_image: data.tw_setting.exclude_default_profile_image,
        exclude_no_description: data.tw_setting.exclude_no_description,
        exclude_unfollowed: data.tw_setting.exclude_unfollowed,
        min_followers: data.tw_setting.min_followers,
        daily_send_limit_count: data.tw_setting.daily_send_limit_count,
        min_tweets: data.tw_setting.min_tweets,
        exclude_description_keyword: data.tw_setting.exclude_description_keyword,
        layout_type: layoutType,
        custom_layout: customLayout,
        page_main_image_url: basicLayout?.page_main_image_url ?? data.tw_setting.page_main_image_url ?? '',
        page_main_image_origin_name:
          basicLayout?.page_main_image_origin_name ?? data.tw_setting.page_main_image_origin_name ?? '',
        page_title: basicLayout?.page_title ?? data.tw_setting.page_title ?? '',
        page_text: basicLayout?.page_text ?? data.tw_setting.page_text ?? '',
        page_rules: basicLayout?.page_rules ?? data.tw_setting.page_rules ?? '',
        page_hash: data.tw_setting.page_hash,
        is_browser_guide_enabled: basicLayout?.is_browser_guide_enabled ?? false
      }
    }

    if (data.target_sns === 'instagram' && data.type === 'instantwin' && data.in_instantwin_setting) {
      form_data = {
        ...form_data,
        is_daily_lottery: Boolean(data.in_instantwin_setting.is_daily_lottery),
        exclude_multi_winner: Boolean(data.in_instantwin_setting.exclude_multi_winner),
        exclude_other_campaign_winner: Boolean(data.in_instantwin_setting.exclude_other_campaign_winner)
      }
    }

    form.value = form_data
    api_campaign.value = data as any

    return response
  }

  const fetchCampaignAccounts = async (): Promise<GetCampaignAccountsResponse> => {
    const groupId = systemStore.requireCurrentGroupId()
    const response: GetCampaignAccountsResponse = await API.get('v1/campaign_accounts', {
      params: { group_id: groupId }
    })

    if (response.data) {
      api_accounts.value = response.data
    }

    return response
  }

  const getDefaultKeywords = async (): Promise<void> => {
    const groupId = systemStore.requireCurrentGroupId()
    const response: GetProfileKeywordSettingsResponse = await API.get('v1/profile_keyword_settings', {
      params: {
        group_id: groupId,
        is_active: true
      }
    })

    if (!response || !response.data) {
      return
    }

    const active_keywords = response.data.filter(v => v.is_active)

    const no_keywords: string[] = []

    default_positive_keyword = active_keywords
      .reduce((acc, v) => [...acc, ...v.positive_keyword], no_keywords)
      .filter((v, index, keywords) => keywords.indexOf(v) === index)

    default_negative_keyword = active_keywords
      .reduce((acc, v) => [...acc, ...v.negative_keyword], no_keywords)
      .filter((v, index, keywords) => keywords.indexOf(v) === index)

    form.value = {
      ...form.value,
      is_keyword_judgment: true,
      positive_keyword: default_positive_keyword,
      negative_keyword: default_negative_keyword
    }
  }

  const setCampaignId = async (payload: number): Promise<void> => {
    campaign_id.value = payload
  }

  const setForm = async (payload: CampaignForm): Promise<void> => {
    form.value = payload
  }

  const setMode = async (payload: 'create' | 'edit' | 'copy'): Promise<void> => {
    mode.value = payload
  }

  const nextStep = async (): Promise<void> => {
    switch (step.value) {
      case 0:
        TrackingService.sendEvent('click:キャンペーンの新規作成|アカウント|次へ')
        break
      case 1:
        TrackingService.sendEvent('click:キャンペーンの新規作成|タイトル|次へ')
        break
      case 2:
        TrackingService.sendEvent('click:キャンペーンの新規作成|期間|次へ')
        break
      case 3:
        if (form.value.target_sns === 'twitter') {
          TrackingService.sendEvent(`click:キャンペーンの新規作成|応募条件(X)|次へ`)
        } else TrackingService.sendEvent(`click:キャンペーンの新規作成|応募条件(IG)|次へ`)
        break
      default:
        break
    }

    if (step.value >= 4) {
      return
    }

    step.value = step.value + 1
  }

  const prevStep = async (): Promise<void> => {
    switch (step.value) {
      case 1:
        TrackingService.sendEvent('click:キャンペーンの新規作成|タイトル|戻る')
        break
      case 2:
        TrackingService.sendEvent('click:キャンペーンの新規作成|期間|戻る')
        break
      case 3:
        if (form.value.target_sns === 'twitter') {
          TrackingService.sendEvent(`click:キャンペーンの新規作成|応募条件(X)|戻る`)
        } else TrackingService.sendEvent(`click:キャンペーンの新規作成|応募条件(IG)|戻る`)
        break
      case 4:
        TrackingService.sendEvent('click:キャンペーンの新規作成|確認|戻る')
        break
      default:
        break
    }

    if (step.value <= 0) {
      return
    }

    step.value = step.value - 1
  }

  const createCampaign = async (): Promise<PostCampaignResponse> => {
    const f = form.value

    let settings = {}

    if (f.target_sns === 'twitter') {
      const account = api_accounts.value.find(v => v.id === f.account_id)
      const account_screen_name = account ? account.screen_name : null

      // キーワード入力欄を隠している場合はフォームに残った値を送らない
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: is_keyword_input_hidden.value ? [] : f.keywords,
        or_keyword: is_keyword_input_hidden.value ? [] : f.or_keywords,
        positive_keyword: f.is_keyword_judgment ? f.positive_keyword : [],
        negative_keyword: f.is_keyword_judgment ? f.negative_keyword : [],
        is_include_mention: f.is_include_mention,
        is_include_reply: f.is_include_reply,
        campaign_account_screen_name: account_screen_name
      }

      if (f.campaign_type === 'old_web_instantwin') {
        const layoutPayload = buildWebInstantwinLayoutPayload(f)
        settings = {
          ...settings,
          require_follow: f.require_follow,
          exclude_hunter_account: f.exclude_hunter_account,
          exclude_account_create_date_within_period: f.exclude_account_create_date_within_period,
          exclude_default_profile_image: f.exclude_default_profile_image,
          exclude_no_description: f.exclude_no_description,
          exclude_unfollowed: f.exclude_unfollowed,
          min_followers: f.min_followers,
          min_tweets: f.min_tweets,
          exclude_description_keyword: f.exclude_description_keyword,
          ...layoutPayload
        }
      }
    } else if (f.target_sns === 'instagram' && f.campaign_type === 'instantwin') {
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: f.keywords,
        is_daily_lottery: Boolean(f.is_daily_lottery),
        exclude_multi_winner: Boolean(f.exclude_multi_winner),
        exclude_other_campaign_winner: Boolean(f.exclude_other_campaign_winner)
      }
    } else {
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: f.keywords
      }
    }

    const settingMap = {
      twitter: 'tw_setting',
      instagram: f.campaign_type === 'instantwin' ? 'in_instantwin_setting' : 'in_setting',
      tiktok: 'tt_setting'
    }

    const groupId = systemStore.requireCurrentGroupId()

    const params = {
      group_id: groupId,
      account_id: f.account_id,
      target_sns: f.target_sns,
      title: f.title,
      start_datetime: moment(`${f.start_date} ${f.start_time}`, 'YYYY-MM-DD HH:mm').format(),
      end_datetime: moment(`${f.end_date} ${f.end_time}`, 'YYYY-MM-DD HH:mm').format(),
      campaign_type: f.campaign_type,
      [settingMap[f.target_sns]]: settings
    }

    is_loading.value = true

    const response: PostCampaignResponse = await API.post('v1/campaigns', params)

    is_loading.value = false

    if (response.data && response.data.id) {
      campaign_id.value = response.data.id
    }

    return response
  }

  const updateCampaign = async (): Promise<PutCampaignResponse> => {
    const f = form.value

    let settings = {}

    if (f.target_sns === 'twitter') {
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: f.keywords,
        or_keyword: f.or_keywords,
        positive_keyword: f.is_keyword_judgment ? f.positive_keyword : [],
        negative_keyword: f.is_keyword_judgment ? f.negative_keyword : [],
        is_include_mention: f.is_include_mention,
        is_include_reply: f.is_include_reply
      }

      if (f.campaign_type === 'old_web_instantwin') {
        const layoutPayload = buildWebInstantwinLayoutPayload(f)
        settings = {
          ...settings,
          require_follow: f.require_follow,
          exclude_hunter_account: f.exclude_hunter_account,
          exclude_account_create_date_within_period: f.exclude_account_create_date_within_period,
          exclude_default_profile_image: f.exclude_default_profile_image,
          exclude_no_description: f.exclude_no_description,
          exclude_unfollowed: f.exclude_unfollowed,
          min_followers: f.min_followers,
          min_tweets: f.min_tweets,
          exclude_description_keyword: f.exclude_description_keyword,
          ...layoutPayload
        }
      }
    } else if (f.target_sns === 'instagram' && f.campaign_type === 'instantwin') {
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: f.keywords,
        is_daily_lottery: Boolean(f.is_daily_lottery),
        exclude_multi_winner: Boolean(f.exclude_multi_winner),
        exclude_other_campaign_winner: Boolean(f.exclude_other_campaign_winner)
      }
    } else {
      settings = {
        entry_action_type: f.entry_action_type,
        search_keyword: f.keywords
      }
    }

    const settingMap = {
      twitter: 'tw_setting',
      instagram: f.campaign_type === 'instantwin' ? 'in_instantwin_setting' : 'in_setting',
      tiktok: 'tt_setting'
    }

    const params = {
      target_sns: f.target_sns,
      title: f.title,
      start_datetime: moment(`${f.start_date} ${f.start_time}`, 'YYYY-MM-DD HH:mm').format(),
      end_datetime: moment(`${f.end_date} ${f.end_time}`, 'YYYY-MM-DD HH:mm').format(),
      campaign_type: f.campaign_type,
      [settingMap[f.target_sns]]: settings
    }

    is_loading.value = true

    const response: PutCampaignResponse = await API.put(`v1/campaigns/${campaign_id.value}`, params)

    is_loading.value = false

    return response
  }

  const deleteCampaign = async (): Promise<DeleteCampaignResponse> => {
    is_loading.value = true

    const response: DeleteCampaignResponse = await API.delete(`v1/campaigns/${campaign_id.value}`)

    is_loading.value = false

    return response
  }

  const checkCampaign = async (payload: {
    start_datetime: string
    end_datetime: string
    exclude_campaign_id?: number
  }): Promise<void> => {
    const groupId = systemStore.requireCurrentGroupId()

    const params = {
      campaign_id: campaign_id.value,
      start_datetime: payload.start_datetime,
      end_datetime: payload.end_datetime,
      group_id: groupId,
      type: 'keyword',
      exclude_campaign_id: payload.exclude_campaign_id ? payload.exclude_campaign_id : 0
    }

    is_validating.value = true

    const response: GetValidationResponse = await API.get('v1/campaigns/validations', { params })

    is_validating.value = false

    if (response.data) {
      is_max_concurrent_campaign.value = false
      return
    }

    is_max_concurrent_campaign.value = true
  }

  const reset = async (): Promise<void> => {
    api_accounts.value = []
    campaign_id.value = null
    form.value = { ...initial_form, custom_layout: createWebInstantwinDefaultCustomLayout(page_rules) }
    is_max_concurrent_campaign.value = false
    mode.value = 'create'
    step.value = 0
    is_loading.value = false
    webiw_background_color_show_hex.value = true
  }

  return {
    api_accounts,
    api_campaign,
    campaign_id,
    form,
    mode,
    step,
    is_loading,
    is_max_concurrent_campaign,
    is_validating,
    webiw_background_color_show_hex,
    // computed (getters)
    is_form_change,
    is_title_max_length,
    is_next_disabled,
    is_campaign_period_empty,
    is_start_datetime_error,
    is_end_datetime_before_start_datetime,
    is_end_date_time_before_now,
    is_edit_disabled,
    is_period_end_after_ntt_aaa_switchover,
    is_web_instantwin_period_invalid,
    ntt_aaa_switchover_date_text,
    is_url_setting_notice_target,
    is_keyword_input_hidden,
    is_long_post_notice_target,
    // actions
    fetchCampaign,
    fetchCampaignAccounts,
    getDefaultKeywords,
    setCampaignId,
    setForm,
    setMode,
    nextStep,
    prevStep,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    checkCampaign,
    reset
  }
})
